import FinnixProWordmark from '../Brand/FinnixProWordmark';
import AppearanceControl from '../Shared/AppearanceControl';
import React, { useState, useRef, useEffect } from 'react';
import { AppBar, Box, Button, Collapse, Divider, Drawer, IconButton, List, ListItemButton, ListItemText, Toolbar, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { preservePublicCsvDemoContext } from '../../utils/publicCsvDemoState';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { PublicContainer } from './Marketing/PublicLayout';
import { publicSpacing } from './Marketing/marketingPageStyles';

export const marketingNavigation = [['Pricing', '/select-plan']];
const capabilities = {
  Product: [
    ['Project Economics', '/product/features#project-economics', 'Actual spend and expected completion cost.'],
    ['Portfolio Risk', '/product/features#portfolio-risk', 'Where financial exposure is concentrated.'],
    ['Revenue / ARR Exposure', '/product/features#revenue-exposure', 'Recurring and commercial value tied to delivery.'],
    ['Accountability', '/product/features#accountability', 'What needs action and who owns the next step.'],
    ['Product overview', '/product/overview', 'Explore the complete financial review.'],
  ],
  Platform: [
    ['Import & Refresh', '/product/how-it-works#import-refresh', 'Turn project financial data into a current review.'],
    ['Financial Intelligence', '/product/features#portfolio-risk', 'Trace exposure to its inputs and reasons.'],
    ['Commercial Intelligence', '/product/features#revenue-exposure', 'Connect commitments to delivery readiness.'],
    ['Data & Security', '/product/security', 'Sources, access, and data handling.'],
  ],
  Solutions: [
    ['Finance', '/solutions/finance-team', 'Connect delivery to the financial outlook.'],
    ['Delivery', '/solutions/delivery-team', 'Understand delivery’s financial implications.'],
    ['Operations', '/solutions/ops-team', 'Prioritize portfolio intervention.'],
    ['Professional Services', '/use-cases/professional-services', 'Understand the economics of client delivery.'],
  ],
  Resources: [
    ['Demo', '/resources/demo', 'Review a sample or your project CSV.'],
    ['How It Works', '/product/how-it-works', 'Follow the review from data to action.'],
    ['Security', '/product/security', 'Review access controls and data handling.'],
    ['Newsletter / Insights', '/resources/newsletter', 'Product updates and financial perspectives.'],
  ],
};
export const groups = {
  Product: [...capabilities.Product.filter(([name]) => name !== 'Product overview'), ...capabilities.Platform],
  Solutions: capabilities.Solutions,
  Resources: capabilities.Resources,
};
const productColumns = [
  { label: 'Core capabilities', items: groups.Product.slice(0, 4) },
  { label: 'Supporting workflows', items: groups.Product.slice(4) },
];
function DesktopGroup({ label, items, active, setActive, location }) {
  const trigger = useRef(null);
  const panel = useRef(null);
  const suppressFocus = useRef(false);
  const expanded = active === label;
  const dismiss = () => { suppressFocus.current = document.activeElement !== trigger.current; setActive(null); trigger.current?.focus(); };
  return <Box onMouseEnter={() => setActive(label)} onMouseLeave={event => {
    if (!event.currentTarget.contains(document.activeElement)) setActive(null);
  }} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setActive(null); }}
    onKeyDown={event => {
      if (event.key === 'Escape') { event.preventDefault(); dismiss(); }
      if (event.key === 'ArrowDown' && event.target === trigger.current) { event.preventDefault(); setActive(label); requestAnimationFrame(() => panel.current?.querySelector('a')?.focus()); }
    }} sx={{ position: 'relative' }}>
    <Button ref={trigger} id={`nav-${label}`} aria-expanded={expanded} aria-controls={`panel-${label}`}
      onFocus={() => { if (suppressFocus.current) suppressFocus.current = false; else setActive(label); }}
      onClick={() => setActive(label)} endIcon={<ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none' }} />}
      sx={{ color: 'text.primary', minHeight: 44, px: 1.25 }}>{label}</Button>
    <Box ref={panel} id={`panel-${label}`} aria-labelledby={`nav-${label}`} hidden={!expanded}
      sx={{ position: 'absolute', top: '100%', left: label === 'Resources' ? 'auto' : 0, right: label === 'Resources' ? 0 : 'auto', pt: 1, width: label === 'Product' ? 820 : 390 }}>
      <Box sx={{ p: 2.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2.5, boxShadow: '0 12px 32px rgba(16,62,64,0.12)' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: label === 'Product' ? '1fr 1fr' : '1fr', gap: 2 }}>
          {(label === 'Product' ? productColumns : [{ label, items }]).map((column, index) => <Box key={column.label} sx={index ? { pl: 2, borderLeft: '1px solid', borderColor: 'divider' } : {}}>
            <Typography sx={{ px: 1.5, pb: 1, fontSize: '0.7rem', fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: 'primary.main' }}>{column.label}</Typography>
            {column.items.map(([name, to, description]) => <Box key={to} component={RouterLink} to={to}
              aria-current={location.pathname + location.hash === to ? 'page' : undefined}
              onClick={() => setActive(null)} sx={{ display: 'block', textDecoration: 'none', color: 'text.primary', px: 1.5, py: 1.5, borderRadius: 1.5, '&:hover, &:focus-visible': { bgcolor: 'action.hover' }, '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 } }}>
              <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700 }}>{name}</Typography>
              <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.5, color: 'text.secondary', mt: 0.5 }}>{description}</Typography>
            </Box>)}
          </Box>)}
        </Box>
        {label === 'Product' && <Button component={RouterLink} to="/product/overview" onClick={() => setActive(null)} sx={{ mt: 1.5, pt: 1.5, px: 1.5, width: '100%', justifyContent: 'flex-start', borderTop: '1px solid', borderColor: 'divider', borderRadius: 0 }}>Product overview →</Button>}
      </Box>
    </Box>
  </Box>;
}
export default function MarketingNav({ isAuthenticated = false }) {
  const location = useLocation();
  const navRoot = useRef(null);
  useEffect(() => {
    const closeOutside = event => { if (!navRoot.current?.contains(event.target)) setMenu(null); };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, []);
  const preserveDemo = () => { if (location.pathname === '/resources/demo') preservePublicCsvDemoContext(); };
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const closeMobile = () => { setOpen(false); setExpandedGroup(null); };
  useEffect(() => { setMenu(null); setOpen(false); }, [location.pathname, location.hash]);
  const mobileRowSx = { minHeight: 44, px: 1.5, py: 0.75, borderRadius: 1 };
  const mobileTextProps = { fontSize: '0.9375rem', fontWeight: 600 };
  return <AppBar ref={navRoot} position="sticky" elevation={0} color="inherit" sx={{ bgcolor: 'background.paper', boxShadow: theme => `inset 0 -1px 0 ${theme.palette.divider}` }}>
    <PublicContainer><Toolbar disableGutters sx={{ gap: 2, minHeight: publicSpacing.navHeight, justifyContent: 'space-between' }}>
      <Box component={RouterLink} to="/" aria-label="FinnixPro home" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.7, textDecoration: 'none', color: 'primary.main' }}><FinnixProWordmark /></Box>
      <Box component="nav" aria-label="Main navigation" sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 0.5 }}>
        {Object.entries(groups).map(([label, items]) => <DesktopGroup key={label} label={label} items={items} active={menu} setActive={setMenu} location={location} />)}
        {marketingNavigation.map(([label, to]) => <Button key={to} component={RouterLink} to={to} sx={{ color: 'text.primary' }}>{label}</Button>)}
      </Box>
      <Box sx={{ display: { xs: 'none', lg: 'flex' }, gap: 1 }}><Button component={RouterLink} to={isAuthenticated ? '/dashboard' : '/login'} onClick={preserveDemo}>{isAuthenticated ? 'Portfolio Risk' : 'Sign In'}</Button><Button component={RouterLink} to="/resources/demo" variant="contained" sx={{ color: 'common.white', borderRadius: 99, px: 2.5 }}>Try with your data</Button></Box>
      <IconButton aria-label="Open navigation" onClick={() => setOpen(true)} sx={{ display: { lg: 'none' }, width: 44, height: 44 }}><MenuIcon /></IconButton>
      <Drawer anchor="right" open={open} onClose={closeMobile}
        PaperProps={{ sx: { width: 340, maxWidth: 'calc(100vw - 24px)', bgcolor: 'background.paper' } }}>
        <Box component="nav" aria-label="Mobile navigation" sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" fontWeight={700}>Menu</Typography>
            <IconButton aria-label="Close navigation" onClick={closeMobile} sx={{ width: 44, height: 44 }}><CloseIcon /></IconButton>
          </Box>
          <Box sx={{ px: 2, py: 1.5, flex: 1, overflowY: 'auto' }}>
            <List disablePadding>
              {Object.entries(groups).map(([label, items]) => {
                const expanded = expandedGroup === label;
                return <React.Fragment key={label}>
                  <ListItemButton component="button" onClick={() => setExpandedGroup(expanded ? null : label)}
                    aria-expanded={expanded} aria-controls={`mobile-${label}`} sx={{ ...mobileRowSx, width: '100%', textAlign: 'left' }}>
                    <ListItemText primary={label} primaryTypographyProps={mobileTextProps} />
                    <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }} />
                  </ListItemButton>
                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <List id={`mobile-${label}`} aria-label={label} disablePadding sx={{ ml: 1.5, pl: 1, mb: 1, borderLeft: '1px solid', borderColor: 'divider' }}>
                      {items.map(([name, to], index) => <React.Fragment key={name}>
                        {label === 'Product' && [0, 4].includes(index) && <Typography sx={{ px: 1.5, pt: 1.5, pb: 0.5, color: 'text.secondary', fontSize: '0.75rem', fontWeight: 700 }}>{index === 0 ? 'Core capabilities' : 'Supporting workflows'}</Typography>}
                        <ListItemButton component={RouterLink} to={to} key={to}
                        selected={location.pathname === to} aria-current={location.pathname === to ? 'page' : undefined}
                        onClick={closeMobile} sx={mobileRowSx}>
                        <ListItemText primary={name} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                      </ListItemButton></React.Fragment>)}
                      {label === 'Product' && <ListItemButton component={RouterLink} to="/product/overview" onClick={closeMobile} sx={mobileRowSx}><ListItemText primary="Product overview" primaryTypographyProps={{ fontSize: '0.875rem' }} /></ListItemButton>}
                    </List>
                  </Collapse>
                </React.Fragment>;
              })}
              <Divider sx={{ my: 1 }} />
              {marketingNavigation.map(([label, to]) => <ListItemButton component={RouterLink} to={to} key={to}
                selected={location.pathname === to} aria-current={location.pathname === to ? 'page' : undefined}
                onClick={closeMobile} sx={mobileRowSx}>
                <ListItemText primary={label} primaryTypographyProps={mobileTextProps} />
              </ListItemButton>)}
            </List>
          </Box>
          <Box sx={{ mx: 2.5, px: 1.5, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
            <Typography variant="body2" fontWeight={600}>Appearance</Typography>
            <AppearanceControl />
          </Box>
          <Box sx={{ mt: 2, p: 2.5, pt: 0, flexShrink: 0, pb: 'max(20px, env(safe-area-inset-bottom))' }}>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <Button component={RouterLink} to={isAuthenticated ? '/dashboard' : '/login'} variant="outlined" fullWidth
                onClick={() => { preserveDemo(); closeMobile(); }}>{isAuthenticated ? 'Portfolio Risk' : 'Sign In'}</Button>
              <Button component={RouterLink} to="/signup" variant="outlined" fullWidth
                onClick={() => { preserveDemo(); closeMobile(); }}>Get Started</Button>
            </Box>
            <Button component={RouterLink} to="/resources/demo" onClick={closeMobile} variant="contained" fullWidth>Try with your data</Button>
          </Box>
        </Box>
      </Drawer>
    </Toolbar></PublicContainer>
  </AppBar>;
}
